import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import axios from 'axios';
import Pagination from '../../component/Admin/Pagination';
import { FaEye, FaFileExcel, FaFilter, FaPlusCircle, FaTrashAlt, FaUndoAlt, FaUsers, FaChevronDown, FaChevronUp, FaSearch } from 'react-icons/fa';
import Swal from 'sweetalert2';
import TableBasic from '../../component/Admin/TableBasic';
import { canAccess } from '../../helper/permissions';

const Loa = ({ data }) => {
    const navigate = useNavigate();
    const header = {
        headers: { 'Authorization': `Bearer ${data.token}` }
    };
    const permissions = data?.user?.permissions || {};
    const canCreate = canAccess(permissions, 'loa', 'create');
    const canDelete = canAccess(permissions, 'loa', 'delete');

    const [listLoa, setListLoa] = useState([]);
    const [listJournal, setListJournal] = useState([]);
    const [meta, setMeta] = useState({});
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [search, setSearch] = useState('');
    const [selectedJournal, setSelectedJournal] = useState('');
    const [filterVolume, setFilterVolume] = useState('');
    const [filterIssue, setFilterIssue] = useState('');
    const [filterMonth, setFilterMonth] = useState('');
    const [filterYear, setFilterYear] = useState('');
    const [filterVolumes, setFilterVolumes] = useState([]);
    const [filterIssues, setFilterIssues] = useState([]);
    const [filterMonths, setFilterMonths] = useState([]);
    const [filterYears, setFilterYears] = useState([]);
    const [filterPaymentStatus, setFilterPaymentStatus] = useState('');
    const [isFilterCollapsed, setIsFilterCollapsed] = useState(true);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ASC' });
    const loaRequestIdRef = useRef(0);

    useEffect(() => {
        getListLoa();
        // eslint-disable-next-line
    }, [page, limit, search, selectedJournal, filterVolume, filterIssue, filterMonth, filterYear, filterPaymentStatus, sortConfig]);

    useEffect(() => {
        getListJournal();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (selectedJournal) {
            axios.get(`api/issues/unique-values/${selectedJournal}`, header)
                .then((response) => {
                    const data = response.data || {};
                    setFilterVolumes(data.volumes || []);
                    setFilterIssues(data.issues || []);
                    setFilterMonths(data.months || []);
                    setFilterYears(data.years || []);
                })
                .catch((error) => {
                    console.error('Error fetching unique values:', error);
                    setFilterVolumes([]);
                    setFilterIssues([]);
                    setFilterMonths([]);
                    setFilterYears([]);
                });
        } else {
            setFilterVolumes([]);
            setFilterIssues([]);
            setFilterMonths([]);
            setFilterYears([]);
        }
        // eslint-disable-next-line
    }, [selectedJournal]);

    const getListLoa = () => {
        const requestId = loaRequestIdRef.current + 1;
        loaRequestIdRef.current = requestId;
        const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
        const journalParam = selectedJournal ? `&journal=${selectedJournal}` : '';
        const volumeParam = filterVolume ? `&volume=${encodeURIComponent(filterVolume)}` : '';
        const issueParam = filterIssue ? `&issue=${encodeURIComponent(filterIssue)}` : '';
        const monthParam = filterMonth ? `&month=${encodeURIComponent(filterMonth)}` : '';
        const yearParam = filterYear ? `&year=${encodeURIComponent(filterYear)}` : '';
        const paymentStatusParam = filterPaymentStatus ? `&paymentStatus=${encodeURIComponent(filterPaymentStatus)}` : '';
        const sortParam = sortConfig?.key ? `&sortBy=${encodeURIComponent(sortConfig.key)}&sortDir=${encodeURIComponent(sortConfig.direction)}` : '';
        axios.get(`api/loa?page=${page}&limit=${limit}${searchParam}${journalParam}${volumeParam}${issueParam}${monthParam}${yearParam}${paymentStatusParam}${sortParam}`, header)
            .then((response) => {
                if (requestId !== loaRequestIdRef.current) {
                    return;
                }
                setMeta(response.data.meta);
                setListLoa(response.data.items);
            })
            .catch((error) => {
                if (requestId !== loaRequestIdRef.current) {
                    return;
                }
                console.error('Error fetching data:', error);
            });
    };

    const getListJournal = () => {
        axios.get(`api/journal?page=1&limit=100`, header)
            .then((response) => {
                setListJournal(response.data.items);
            })
            .catch((error) => {
                console.error('Error fetching data:', error);
            });
    };

    const selectPage = (value) => {
        setPage(Number(value));
    };

    const nextPage = () => {
        if (page < meta.totalPages) {
            setPage(page + 1);
        }
    };

    const prevPage = () => {
        if (page > 1) {
            setPage(page - 1);
        }
    };

    const limitPage = (event) => {
        setLimit(Number(event.target.value));
        setPage(1);
    };

    const handleFilterJournalChange = (event) => {
        setSelectedJournal(event.target.value);
        setFilterVolume('');
        setFilterIssue('');
        setFilterMonth('');
        setFilterYear('');
        setPage(1);
    };

    const handleResetFilters = () => {
        setSelectedJournal('');
        setSearch('');
        setFilterVolume('');
        setFilterIssue('');
        setFilterMonth('');
        setFilterYear('');
        setFilterPaymentStatus('');
        setPage(1);
    };

    const handleExportExcel = async () => {
        try {
            const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
            const journalParam = selectedJournal ? `&journal=${selectedJournal}` : '';
            const volumeParam = filterVolume ? `&volume=${encodeURIComponent(filterVolume)}` : '';
            const issueParam = filterIssue ? `&issue=${encodeURIComponent(filterIssue)}` : '';
            const monthParam = filterMonth ? `&month=${encodeURIComponent(filterMonth)}` : '';
            const yearParam = filterYear ? `&year=${encodeURIComponent(filterYear)}` : '';
            const paymentStatusParam = filterPaymentStatus ? `&paymentStatus=${encodeURIComponent(filterPaymentStatus)}` : '';
            const sortParam = sortConfig?.key ? `&sortBy=${encodeURIComponent(sortConfig.key)}&sortDir=${encodeURIComponent(sortConfig.direction)}` : '';
            const exportLimit = process.env.EXPORT_LIMIT || 2000;
            const { data: resp } = await axios.get(
                `api/loa?page=1&limit=${exportLimit}${searchParam}${journalParam}${volumeParam}${issueParam}${monthParam}${yearParam}${paymentStatusParam}${sortParam}`,
                header
            );
            const rows = (resp?.items || listLoa || []).map((item, idx) => ({
                No: idx + 1,
                ID: item?.submitID || '',
                Title: item?.title || '',
                Authors: item?.author || '',
                Journal: item?.journal_id?.journal_name || '',
                Volume: item?.volume ?? '',
                Issue: item?.issue ?? '',
                Month: item?.month || '',
                Year: item?.year ?? '',
                Payment: item?.paymentStatus || '-',
                TrxID: item?.trxID || '-',
            }));
            const worksheet = XLSX.utils.json_to_sheet(rows);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'LOA');
            const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
            const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const ts = new Date().toISOString().replace(/[:.-]/g, '');
            saveAs(blob, `loa_export_${ts}.xlsx`);
        } catch (error) {
            console.error('Error exporting Excel:', error);
            Swal.fire('Gagal', 'Terjadi kesalahan saat export Excel', 'error');
        }
    };

    const handleClickDelete = (id) => {
        const found = listLoa.find(item => item.loa_id === id);
        if (!found) return;

        if (found.paymentStatus === 'lunas') {
            Swal.fire('Tidak diizinkan', 'LOA dengan payment lunas tidak dapat dihapus.', 'warning');
            return;
        }

        Swal.fire({
            title: 'Hapus LOA?',
            text: 'Data yang dihapus tidak dapat dikembalikan.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#4569ab',
            cancelButtonColor: '#5C5E62',
            confirmButtonText: 'Ya, hapus'
        }).then((result) => {
            if (result.isConfirmed) {
                axios.delete(`api/loa/${id}`, header)
                    .then(() => {
                        getListLoa();
                        Swal.fire({
                            title: 'Deleted',
                            text: 'Data berhasil dihapus',
                            icon: 'success',
                            timer: 1000,
                            showConfirmButton: true
                        });
                    })
                    .catch((error) => {
                        console.error('Error deleting data:', error);
                        const message =
                            error?.response?.data?.message ||
                            'Terjadi kesalahan saat menghapus data.';
                        Swal.fire('Gagal!', message, 'error');
                    });
            }
        });
    };

    const head = [
        { name: 'No.', align: 'center' },
        { name: 'ID', align: 'left', key: 'submitID', sortable: true },
        { name: 'Title', align: 'left', key: 'title', sortable: true },
        { name: 'Journal', align: 'left', key: 'journal_id', sortable: true },
        { name: 'Payment', align: 'left', key: 'paymentTrxID', sortable: true },
        { name: 'Action', align: 'center', sticky: true },
    ];

    const handleSort = (key) => {
        setSortConfig((prev) => {
            if (prev.key === key) {
                const nextDirection = prev.direction === 'ASC' ? 'DESC' : 'ASC';
                return { key, direction: nextDirection };
            }
            return { key, direction: 'ASC' };
        });
        setPage(1);
    };

    return (
        <>
            <div className="space-y-4">
                <div className="card overflow-hidden">
                    <div className="card-body toolbar-row !py-4">
                        <div className="toolbar-search relative">
                            <FaSearch className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted" />
                            <input
                                id="search"
                                value={search}
                                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                                type="text"
                                placeholder="Cari LOA..."
                                className="input w-full !pl-9"
                            />
                        </div>

                        <button
                            type="button"
                            onClick={() => setIsFilterCollapsed(!isFilterCollapsed)}
                            className={`toolbar-action pill ${!isFilterCollapsed ? 'pill-active' : 'pill-inactive'}`}
                        >
                            <FaFilter className="h-3.5 w-3.5" />
                            Filter
                            {isFilterCollapsed ? (
                                <FaChevronDown className="h-3 w-3 opacity-80" />
                            ) : (
                                <FaChevronUp className="h-3 w-3 opacity-80" />
                            )}
                        </button>

                        {canCreate ? (
                            <button
                                onClick={() => navigate('/admin/loa/create')}
                                className="btn-primary toolbar-action shrink-0"
                                type="button"
                            >
                                <FaPlusCircle />
                                <span>Tambah LOA</span>
                            </button>
                        ) : null}
                    </div>

                    {!isFilterCollapsed && (
                        <div className="filter-grid">
                            <div className="filter-field">
                                <label htmlFor="journal" className="mb-1.5 block text-left text-xs font-medium text-muted">Journal</label>
                                <select
                                    id="journal"
                                    value={selectedJournal}
                                    onChange={handleFilterJournalChange}
                                    className="input w-full !h-10"
                                >
                                    <option value="">Semua</option>
                                    {listJournal.map((j) => (
                                        <option key={j.journal_id} value={j.journal_id}>{j.journal_name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="filter-field">
                                <label htmlFor="filterPaymentStatus" className="mb-1.5 block text-left text-xs font-medium text-muted">Payment</label>
                                <select
                                    id="filterPaymentStatus"
                                    value={filterPaymentStatus}
                                    onChange={(e) => {
                                        setFilterPaymentStatus(e.target.value);
                                        setPage(1);
                                    }}
                                    className="input w-full !h-10"
                                >
                                    <option value="">Semua</option>
                                    <option value="pending">Pending</option>
                                    <option value="lunas">Lunas</option>
                                </select>
                            </div>
                            {selectedJournal && (
                                <div className="filter-field">
                                    <label htmlFor="filterVolume" className="mb-1.5 block text-left text-xs font-medium text-muted">Volume</label>
                                    <select
                                        id="filterVolume"
                                        value={filterVolume}
                                        onChange={(e) => {
                                            setFilterVolume(e.target.value);
                                            setFilterIssue('');
                                            setFilterMonth('');
                                            setFilterYear('');
                                            setPage(1);
                                        }}
                                        className="input w-full !h-10"
                                    >
                                        <option value="">Semua</option>
                                        {filterVolumes.map((vol, idx) => (
                                            <option key={idx} value={vol}>{vol}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            {selectedJournal && filterVolume && (
                                <div className="filter-field">
                                    <label htmlFor="filterIssue" className="mb-1.5 block text-left text-xs font-medium text-muted">Issue</label>
                                    <select
                                        id="filterIssue"
                                        value={filterIssue}
                                        onChange={(e) => {
                                            setFilterIssue(e.target.value);
                                            setFilterMonth('');
                                            setFilterYear('');
                                            setPage(1);
                                        }}
                                        className="input w-full !h-10"
                                    >
                                        <option value="">Semua</option>
                                        {filterIssues.map((iss, idx) => (
                                            <option key={idx} value={iss}>{iss}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            {selectedJournal && filterVolume && filterIssue && (
                                <div className="filter-field">
                                    <label htmlFor="filterMonth" className="mb-1.5 block text-left text-xs font-medium text-muted">Bulan</label>
                                    <select
                                        id="filterMonth"
                                        value={filterMonth}
                                        onChange={(e) => {
                                            setFilterMonth(e.target.value);
                                            setFilterYear('');
                                            setPage(1);
                                        }}
                                        className="input w-full !h-10"
                                    >
                                        <option value="">Semua</option>
                                        {filterMonths.map((mon, idx) => (
                                            <option key={idx} value={mon}>{mon}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            {selectedJournal && filterVolume && filterIssue && filterMonth && (
                                <div className="filter-field">
                                    <label htmlFor="filterYear" className="mb-1.5 block text-left text-xs font-medium text-muted">Tahun</label>
                                    <select
                                        id="filterYear"
                                        value={filterYear}
                                        onChange={(e) => { setFilterYear(e.target.value); setPage(1); }}
                                        className="input w-full !h-10"
                                    >
                                        <option value="">Semua</option>
                                        {filterYears.map((yr, idx) => (
                                            <option key={idx} value={yr}>{yr}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            <div className="filter-actions">
                                <button
                                    type="button"
                                    onClick={handleResetFilters}
                                    title="Reset filter"
                                    className="btn-pearl inline-flex h-10 w-full items-center justify-center gap-1.5 px-4 sm:w-auto"
                                >
                                    <FaUndoAlt className="h-3.5 w-3.5" />
                                    Reset
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                <TableBasic head={head} onSort={handleSort} sortConfig={sortConfig}>
                    {listLoa.length > 0 ? (
                        listLoa.map((item, index) => (
                            <tr key={index} className="transition-colors duration-tesla hover:bg-surface-soft dark:hover:bg-white/5">
                                <td className="text-center font-medium text-muted">{(page - 1) * limit + index + 1}</td>
                                <td className="text-left text-[10px] font-light dark:text-white">{item.submitID}</td>
                                <td className="text-left">
                                    <div className="line-clamp-2 font-medium text-ink dark:text-white">
                                        {item?.title}
                                    </div>
                                    <small className="inline-flex items-center gap-1 text-muted">
                                        <FaUsers className="text-[#f67a07] text-[10px]" />
                                        <span className="line-clamp-1">{item.author}</span>
                                    </small>
                                </td>
                                <td className="text-left text-[8px] text-body dark:text-white/90">
                                    <div className="flex flex-col leading-tight">
                                        <span className="line-clamp-1  leading-tight text-xs">{item?.journal_id?.journal_name}</span>
                                        {(item?.volume || item?.issue || item?.month || item?.year) && (
                                            <span className="whitespace-nowrap font-light text-[10px] text-muted">
                                                Vol. {item?.volume || '-'}, Issue {item?.issue || '-'}, {item?.month || '-'} {item?.year || ''}
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td className="text-left ">
                                    {item?.paymentTrxID || item?.payment_trxID || item?.paymentID ? (
                                        <div
                                            title={`Status: ${item?.paymentStatus || 'tidak diketahui'}`}
                                            className=" gap-1 flex items-center justify-center text-[10px]"
                                        >
                                            <div>
                                                {item?.paymentTrxID || item?.payment_trxID || 'N/A'}
                                            </div>
                                            <div>|</div>
                                            {item?.paymentStatus ? (
                                                <div className="text-muted">  {item.paymentStatus}</div>
                                            ) : null}
                                        </div>
                                    ) : (
                                        <div className="text-muted-soft">-</div>
                                    )}
                                </td>
                                <td className="sticky right-0 z-10 whitespace-nowrap bg-canvas text-center md:static dark:bg-tile-1">
                                    <div className="inline-flex items-center gap-1">
                                        <button
                                            type="button"
                                            onClick={() => navigate(`/admin/loa/${item.loa_id}`)}
                                            className="btn-primary !min-h-8 !px-2.5 !py-1.5"
                                            aria-label={`Lihat detail ${item.submitID}`}
                                        >
                                            <FaEye />
                                            <span className="hidden text-xs sm:inline">Detail</span>
                                        </button>
                                        {canDelete ? (
                                            <button
                                                type="button"
                                                className={`btn-pearl !min-h-8 !p-2 ${item?.paymentStatus === 'lunas'
                                                    ? 'cursor-not-allowed opacity-40'
                                                    : 'text-muted hover:text-ink dark:hover:text-white'
                                                    }`}
                                                onClick={() => handleClickDelete(item.loa_id)}
                                                disabled={item?.paymentStatus === 'lunas'}
                                                title={
                                                    item?.paymentStatus === 'lunas'
                                                        ? 'Tidak bisa dihapus (payment lunas)'
                                                        : 'Hapus LOA'
                                                }
                                                aria-label={`Hapus ${item.submitID}`}
                                            >
                                                <FaTrashAlt />
                                            </button>
                                        ) : null}
                                    </div>
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan={6} className="!px-6 !py-12 whitespace-nowrap">
                                <div className="text-center">
                                    <p className="text-sm font-medium text-ink dark:text-white">Data tidak ditemukan</p>
                                    <p className="mt-1 text-xs text-muted">Coba ubah kata kunci atau filter pencarian</p>
                                </div>
                            </td>
                        </tr>
                    )}
                </TableBasic>

                <div className="list-footer">
                    <button
                        onClick={handleExportExcel}
                        className="btn-secondary w-full sm:w-auto"
                        type="button"
                    >
                        <FaFileExcel />
                        <span>Export Excel</span>
                    </button>

                    <div className="w-full sm:w-auto">
                        <Pagination
                            currentPage={page}
                            totalPages={meta.totalPages}
                            limit={limit}
                            limitPage={limitPage}
                            selectPage={selectPage}
                            nextPage={nextPage}
                            prevPage={prevPage}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

export default Loa;
